import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const glowCardVariants = cva("relative rounded-xl transition-all", {
  variants: {
    variant: {
      default: "",
      interactive: "",
      stat: "",
    },
    glow: {
      none: "",
      rest: "",
      hover: "",
      active: "",
      emphasis: "",
    },
  },
  defaultVariants: {
    variant: "default",
    glow: "none",
  },
});

interface GlowCardProps
  extends React.ComponentProps<"div">,
    VariantProps<typeof glowCardVariants> {
  interactive?: boolean;
}

function GlowCard({
  className,
  variant = "default",
  glow = "none",
  interactive = false,
  children,
  ...props
}: GlowCardProps) {
  const showGradientBorder =
    glow === "rest" || glow === "hover" || glow === "active" || variant === "interactive";
  const isEmphasis = glow === "emphasis";

  return (
    <div
      data-slot="glow-card"
      data-variant={variant}
      data-glow={glow}
      className={cn(
        "group/glow relative rounded-xl",
        interactive && "cursor-pointer",
        className
      )}
      {...props}
    >
      {/* Glow pseudo-layer (outer glow behind the card) */}
      {(interactive || glow !== "none") && (
        <div
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute -inset-1 rounded-xl opacity-0 blur-2xl transition-opacity duration-300",
            interactive &&
              "group-hover/glow:opacity-[var(--glow-opacity-hover)] group-active/glow:opacity-[var(--glow-opacity-active)]",
            glow === "active" && "opacity-[var(--glow-opacity-active)]",
            glow === "emphasis" &&
              "opacity-[var(--glow-opacity-emphasis)] animate-glow-pulse"
          )}
          style={{ background: "var(--gradient-primary)" }}
        />
      )}

      {/* Gradient border wrapper */}
      {showGradientBorder ? (
        <div
          aria-hidden="true"
          className={cn(
            "absolute inset-0 rounded-xl p-px",
            "opacity-50 transition-opacity",
            interactive && "group-hover/glow:opacity-100"
          )}
          style={{ background: "var(--gradient-primary)" }}
        >
          <div className="h-full w-full rounded-[calc(var(--radius-xl)-1px)] bg-background" />
        </div>
      ) : null}

      {/* Card content */}
      <div
        className={cn(
          "relative rounded-xl",
          variant === "stat" ? "glass-2" : "glass-1",
          isEmphasis && "animate-gradient-shift",
          isEmphasis
            ? "bg-gradient-to-br from-[var(--gradient-start)] via-[var(--gradient-mid)] to-[var(--gradient-end)] text-white"
            : "",
          interactive &&
            "transition-all duration-300 group-hover/glow:-translate-y-0.5 group-hover/glow:shadow-[var(--shadow-lg)]",
          showGradientBorder && "border-transparent"
        )}
      >
        {children}
      </div>
    </div>
  );
}

export { GlowCard, glowCardVariants };
